import { inject, Injectable, Injector } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpResponse } from '@angular/common/http';
import { Observable, from, of, switchMap, throwError } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { LoggerService } from 'src/app/services/infra/logger/logger.service';
import { BrowserEncryptionService } from 'src/app/services/infra/encryption/browser-encryption.service';
import { EncryptedEnvelope, RawEncryptedEnvelope } from 'src/app/services/infra/encryption/encryption.types';
import { StoreService } from 'src/app/services/infra/store/store.service';
import { isOutpostPlatform } from 'src/app/common/platformUtils';

interface EncryptedFieldInfo {
  field: string;
  format: 'string' | 'dict';
}

interface PreparedRequest {
  req: HttpRequest<unknown>;
  privateKey?: CryptoKey;
  encryptedField?: EncryptedFieldInfo;
}

@Injectable()
export class EncryptionInterceptor implements HttpInterceptor {
  private static readonly INTEGRATION_ID_PATTERN = /\/integrations\/([a-f0-9-]+)/i;
  private static readonly ACTION_NAME_PATTERN = /\/(\w+):execute/;

  private static readonly ACTION_FIELD_MAP = new Map<string, EncryptedFieldInfo>([
    ['getTranscript', { field: 'transcript', format: 'string' }],
    ['getPayload', { field: 'payload', format: 'string' }],
    ['getSnippet', { field: 'snippet', format: 'dict' }],
    ['getInvocationResult', { field: 'invocationResult', format: 'dict' }],
    ['getInvocationParams', { field: 'invocationParams', format: 'dict' }],
  ]);

  private readonly encryptionService = inject(BrowserEncryptionService);
  private readonly injector = inject(Injector);
  private readonly logger = inject(LoggerService);

  public intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return this.prepareRequest(request).pipe(
      switchMap(({ req, privateKey, encryptedField }) =>
        next.handle(req).pipe(switchMap((event) => this.maybeDecryptResponse(event, privateKey, encryptedField)))
      )
    );
  }

  private prepareRequest(request: HttpRequest<unknown>): Observable<PreparedRequest> {
    const actionName = this.extractActionName(request.url);
    if (!actionName) {
      return of({ req: request });
    }

    const fieldInfo = EncryptionInterceptor.ACTION_FIELD_MAP.get(actionName);
    if (!fieldInfo) {
      return of({ req: request });
    }

    const integrationId = this.extractIntegrationId(request.url);
    if (!integrationId) {
      return of({ req: request });
    }

    return this.isOutpostRequest(integrationId).pipe(
      switchMap((isOutpost) => {
        if (!isOutpost) {
          return of({ req: request });
        }
        return from(this.encryptionService.generateKeyPair()).pipe(
          map(({ publicKeyBase64, keyPair }) => ({
            req: request.clone({
              setHeaders: {
                'X-Encryption-Key': publicKeyBase64,
              },
            }),
            privateKey: keyPair.privateKey,
            encryptedField: fieldInfo,
          })),
          catchError(() => {
            this.logger.error('Failed to generate encryption key pair', {
              integrationId,
              url: request.url,
            });
            return throwError(() => new Error('Encryption key attachment failed'));
          })
        );
      })
    );
  }

  private extractActionName(url: string): string | null {
    const match = EncryptionInterceptor.ACTION_NAME_PATTERN.exec(url);
    return match ? match[1] : null;
  }

  private extractIntegrationId(url: string): string | null {
    const match = EncryptionInterceptor.INTEGRATION_ID_PATTERN.exec(url);
    return match ? match[1] : null;
  }

  private isOutpostRequest(integrationId: string): Observable<boolean> {
    const storeService = this.injector.get(StoreService);
    return storeService.integrations$.pipe(
      take(1),
      map((integrations) => {
        const integration = integrations.find((i) => i.id === integrationId);
        return isOutpostPlatform(integration?.platform);
      })
    );
  }

  private maybeDecryptResponse(
    event: HttpEvent<unknown>,
    privateKey?: CryptoKey,
    encryptedField?: EncryptedFieldInfo
  ): Observable<HttpEvent<unknown>> {
    if (!(event instanceof HttpResponse) || !privateKey || !encryptedField) {
      return of(event);
    }

    const body = event.body as Record<string, unknown> | null;
    if (!body || typeof body !== 'object') {
      return of(event);
    }

    const fieldValue = body[encryptedField.field];
    if (fieldValue === undefined || fieldValue === null) {
      return of(event);
    }

    const envelope = this.extractEnvelope(fieldValue, encryptedField.format);
    if (!envelope) {
      return of(event);
    }

    return from(this.encryptionService.decrypt(envelope, privateKey)).pipe(
      map((plaintext) => {
        const parsed = JSON.parse(plaintext) as Record<string, unknown>;
        const unwrapped = this.unwrapDecryptedValue(parsed, encryptedField.field);
        return event.clone({
          body: {
            ...body,
            [encryptedField.field]: unwrapped,
          },
        });
      }),
      catchError(() => {
        this.logger.error('Failed to decrypt response', {
          url: (event as HttpResponse<unknown>).url,
        });
        return throwError(() => new Error('Response decryption failed'));
      })
    );
  }

  // TODO: Remove once the backend stops wrapping
  // the decrypted value in a JSON object keyed by
  // the field name (e.g. {"transcript": "..."}).
  // The outpost fetch-content service currently
  // serializes as json.dumps({field: value}) before
  // encrypting — once it encrypts the raw value
  // directly, delete this method and use the parsed
  // plaintext as-is.
  private unwrapDecryptedValue(parsed: Record<string, unknown>, fieldName: string): unknown {
    if (fieldName in parsed) {
      return parsed[fieldName];
    }
    return parsed;
  }

  private extractEnvelope(fieldValue: unknown, format: 'string' | 'dict'): EncryptedEnvelope | null {
    try {
      if (format === 'string') {
        return this.parseEnvelopeFromString(fieldValue as string);
      }
      return this.parseEnvelopeFromDict(fieldValue as Record<string, unknown>);
    } catch {
      return null;
    }
  }

  private parseEnvelopeFromString(value: string): EncryptedEnvelope | null {
    if (typeof value !== 'string') {
      return null;
    }
    const parsed = JSON.parse(value) as Record<string, unknown>;
    if (!this.isRawEnvelope(parsed)) {
      return null;
    }
    return this.toEncryptedEnvelope(parsed);
  }

  private parseEnvelopeFromDict(value: Record<string, unknown>): EncryptedEnvelope | null {
    if (typeof value !== 'object' || value === null) {
      return null;
    }
    const inner = value['encrypted_envelope'] ?? value['encryptedEnvelope'];
    if (!inner) {
      return null;
    }
    if (typeof inner === 'string') {
      const parsed = JSON.parse(inner) as Record<string, unknown>;
      if (!this.isRawEnvelope(parsed)) {
        return null;
      }
      return this.toEncryptedEnvelope(parsed);
    }
    if (typeof inner === 'object' && inner !== null && this.isRawEnvelope(inner)) {
      return this.toEncryptedEnvelope(inner);
    }
    return null;
  }

  private isRawEnvelope(obj: object): obj is RawEncryptedEnvelope {
    return 'encrypted_data_key' in obj && 'iv' in obj && 'ciphertext' in obj && 'algorithm' in obj;
  }

  private toEncryptedEnvelope(raw: RawEncryptedEnvelope): EncryptedEnvelope {
    return {
      encryptedDataKey: raw.encrypted_data_key,
      iv: raw.iv,
      ciphertext: raw.ciphertext,
      algorithm: raw.algorithm,
    };
  }
}
